import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

const API_URL = window.location.origin;
const CLOUDINARY_UPLOAD_PRESET = "trianf_unsigned"; 
const CLOUDINARY_CLOUD_NAME = "dxzsqhaz8";

const Register = () => {
  const sigPad = useRef(null);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [userTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
   
  const initialState = {
    first_name: "", middle_name: "", last_name: "", 
    email: "", password: "", confirmPassword: "", 
    phone: "", avatar_url: "", h_address: "", h_city: "", 
    zip_code: "", h_state: "", country: "", 
    highest_edn: "", teach_qfn: "", bio: "", examiner: "",
    above_18: "", authorized: "", resume_cv: "", 
    hourly_rate: "", currency: "USD",
    subject1: "", subject2: "", sen_area: "", grade_levels: "",
    crim_back_chk: "", fullname: "", role: "tutor", signature: ""
  };

  const [formData, setFormData] = useState(initialState);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setLoading(true);
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`, {
        method: "POST",
        body: data
      });
      const fileData = await res.json();
      setFormData({ ...formData, [e.target.name]: fileData.secure_url });
      setStatus("✅ File uploaded successfully");
    } catch (err) {
      setStatus("❌ File upload failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadSignature = async () => {
    // Guard Clause to prevent "isEmpty" error when pad is unmounted
    if (!sigPad.current || sigPad.current.isEmpty()) {
      return null;
    }

    const signatureImage = sigPad.current.getTrimmedCanvas().toDataURL('image/png');
    const uploadData = new FormData();
    uploadData.append("file", signatureImage);
    uploadData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: "POST", body: uploadData }
      );
      const data = await response.json();
      return data.secure_url;
    } catch (error) {
      console.error("Signature Upload Error:", error);
      return null;
    }
  };

  // Determine max steps dynamically
  const totalSteps = formData.role === 'tutor' ? 6 : 3;

  const nextStep = () => {
    if (step === 1) {
      if (formData.password !== formData.confirmPassword) return setStatus("❌ Passwords do not match");
      if (formData.password.length < 6) return setStatus("❌ Password too short (min 6 chars)");
      
      // Skip tutor steps if role is learner, jump straight to final review (Step 6)
      if (formData.role === 'learner') {
        setStatus('');
        setStep(6);
        return;
      }
    }
    
    setStatus('');
    setStep(step + 1);
  };

  const prevStep = () => {
    // If moving backwards from Review as a learner, jump directly over tutor sections back to Step 2
    if (step === 6 && formData.role === 'learner') {
      setStep(2);
    } else {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('Creating account and profile...');
    
    const signatureUrl = await uploadSignature();
    
    const fullPayload = { 
      ...formData, 
      signature: signatureUrl || formData.signature,
      timezone: userTimeZone,
      is_complete: true,
      approved: formData.role === 'learner' // Automatically approve learners if backend supports it
    };

    try {
      const response = await fetch(`${API_URL}/api/register-tutor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullPayload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Registration failed");
      
      setStatus('✅ Success! Redirecting to login...');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const ReviewItem = ({ label, value }) => (
    <div className="border-b py-1">
      <span className="text-gray-500 text-[10px] uppercase font-bold">{label}</span>
      <p className="text-sm">{value || 'Not provided'}</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto pt-24 mb-10 bg-white rounded-xl shadow-xl p-8 border border-gray-100">    
      <header className="mb-8">
        <h3 className="text-2xl font-bold text-gray-800">{formData.role === 'tutor' ? 'Tutor Registration' : 'Learner Registration'}</h3>
        <p className="text-gray-500 text-sm">Step {displayStep} of {totalSteps}</p>
        <div className="w-full bg-gray-100 h-2 mt-4 rounded-full overflow-hidden">
            <div className="bg-blue-600 h-full transition-all duration-500" style={{ width: `${(displayStep / totalSteps) * 100}%` }} />
        </div>
      </header>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {step === 1 && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <h4 className="font-bold text-blue-600 border-b pb-2">Account Details</h4>
            {/* ROLE SELECTION INSERTED HERE */}
            <div className="flex flex-col gap-2 mb-4">
              <label className="text-sm font-medium text-gray-700">Select Your Role:</label>
              <select 
                name="role" 
                value={formData.role} 
                onChange={handleChange}
                className="p-3 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer"
              >
                <option value="tutor">Tutor (Tutor)</option>
                <option value="learner">Learner (Learner)</option>
              </select>
            </div>
            <div className="space-y-4 animate-in fade-in">       
          <h3 className='font-bold border-b pb-2 text-blue-600'>Section 1: Account & Personal Information</h3>
          
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" name="first_name" placeholder="First Name *" value={formData.first_name} onChange={handleChange} required className="p-2 border rounded" />
               <input type="text" name="middle_name" placeholder="Middle Name" value={formData.middle_name} onChange={handleChange} className="p-2 border rounded" />
              <input type="text" name="last_name" placeholder="Last Name *" value={formData.last_name} onChange={handleChange} required className="p-2 border rounded" />
              
              <div className="md:col-span-2">
                <label className="text-xs font-bold text-gray-600 block mb-1">Profile Photo</label>
                <input 
                  type="file" 
                  name="avatar_url" 
                  onChange={handleFileUpload} 
                  accept="image/*" 
                  className="w-full p-1 border rounded text-sm file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700"
                />
                {formData.avatar_url && <p className="text-[10px] text-green-600 mt-1">Photo Ready: {formData.avatar_url.substring(0, 40)}...</p>}
              </div>
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-3 rounded border border-blue-100">
              <input type="email" name="email" placeholder="Email Address *" value={formData.email} onChange={handleChange} required className="p-2 border rounded md:col-span-2" />
               <input type="password" name="password" placeholder="Create Password *" value={formData.password} onChange={handleChange} required className="p-2 border rounded" />
                <input type="password" name="confirmPassword" placeholder="Confirm Password *" value={formData.confirmPassword} onChange={handleChange} required className="p-2 border rounded" />
                <p className="text-[10px] text-gray-500 md:col-span-2">Password will be used for your TRiANf login.</p>
                </div>                     
              <input type="number" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleChange} className="p-2 border rounded" />
              </div>
          </div>
          </div>
        )}

       {/* STEP 2: ADDRESS */}
               {step === 2 && (
                 <div className="space-y-4">
               <h3 className='font-bold border-b pb-2'>Section 2: Address</h3>
               <input type="text" name="h_address" placeholder="Home Address" value={formData.h_address} onChange={handleChange} required className="w-full p-2 border rounded" />
               <div className="grid grid-cols-2 gap-4">
                 <input type="text" name="h_city" placeholder="City" value={formData.h_city} onChange={handleChange} required className="p-2 border rounded" />
                 <input type="text" name="h_state" placeholder="Your state" value={formData.h_state} onChange={handleChange} className="w-full p-2 border rounded" />
                 <input type="text" name="country" placeholder="Country" value={formData.country} onChange={handleChange} required className="p-2 border rounded" />
               </div>
               <p className="text-xs text-gray-500">Auto-detected Timezone: {userTimeZone}</p>
       </div>
               )}
             {/* STEP 3: PROFESSIONAL INFO (Hidden for Learner) */}
        {step === 3 && formData.role === 'tutor' && (
                 <div className="space-y-4">
               <h3 className='font-bold border-b pb-2'>Section 3: Professional Info</h3>
               <textarea name="bio" placeholder="Brief Biography (max 600 chars)" value={formData.bio} onChange={handleChange} maxLength="600" className="w-full p-2 border rounded h-24" />        
               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <input type="text" name="highest_edn" placeholder="Highest Education" value={formData.highest_edn} onChange={handleChange} required className="p-2 border rounded" />
               <input type="text" name="teach_qfn" placeholder="State Teaching Qualification" value={formData.teach_qfn} onChange={handleChange} className="w-full p-2 border rounded" />
               <div className="flex flex-col">
               <label className="text-xs font-bold text-gray-600 mb-1">Hourly Rate & Currency</label>
               <div className="flex gap-2">
                 <select 
                   name="currency" 
                   value={formData.currency} 
                   onChange={handleChange}
                   className="p-2 border rounded bg-gray-50 font-bold"
                 >
                   {/* Major Global Currencies */}
         <option value="USD">USD ($)</option>
         <option value="EUR">EUR (€)</option>
         <option value="GBP">GBP (£)</option>
         <option value="CAD">CAD ($)</option>
         <option value="AUD">AUD ($)</option>
         <option value="JPY">JPY (¥)</option>
         <option value="CNY">CNY (¥)</option>
       
         {/* African Currencies */}
         <option value="UGX">UGX (USh)</option>
         <option value="ZAR">ZAR (R)</option>
         <option value="NGN">NGN (₦)</option>
         <option value="KES">KES (KSh)</option>
         <option value="GHS">GHS (GH₵)</option>
         <option value="EGP">EGP (E£)</option>
       
         {/* Asian & Middle Eastern Currencies */}
         <option value="INR">INR (₹)</option>
         <option value="AED">AED (د.إ)</option>
         <option value="SAR">SAR (﷼)</option>
         <option value="SGD">SGD ($)</option>
         <option value="HKD">HKD ($)</option>
         <option value="PKR">PKR (₨)</option>
       
         {/* Other Regions */}
         <option value="BRL">BRL (R$)</option>
         <option value="MXN">MXN ($)</option>
         <option value="NZD">NZD ($)</option>
         <option value="CHF">CHF (CHf)</option>
                 </select>
                 <input 
                   type="number" 
                   name="hourly_rate" 
                   placeholder="Rate" 
                   value={formData.hourly_rate} 
                   onChange={handleChange} 
                   required 
                   className="p-2 border rounded flex-1" 
                 />
               </div>
             </div>
               
               <input type="text" name="examiner" placeholder="Are you an Examiner" value={formData.examiner} onChange={handleChange} required className="w-full p-2 border rounded" />
               <input type="text" name="above_18" placeholder="Are you Above 18 Years" value={formData.above_18} onChange={handleChange} required className="w-full p-2 border rounded" />           
               <input type="text" name="authorized" placeholder="Are you authorized to work in the country" value={formData.authorized} onChange={handleChange} required className="w-full p-2 border rounded" />           
               <div>
                     <label className="text-xs font-bold text-gray-600 block mb-1">Upload Resume/CV (PDF/Doc)</label>
                     <input 
                       type="file" 
                       name="resume_cv" 
                       onChange={handleFileUpload} 
                       accept=".pdf,.doc,.docx"
                       className="w-full p-1 border rounded text-sm file:bg-blue-50 file:text-blue-700"
                     />
                     {formData.resume_cv && <p className="text-[10px] text-green-600 mt-1">Document Ready!</p>}
                   </div>
               </div>
               </div>
               )}  
            {/* STEP 4: TUTORING SPECIFICS (Hidden for Learner) */}
        {step === 4 && formData.role === 'tutor' && (
               <div className="space-y-4">   
               <h3 className='font-bold border-b pb-2'>Section 4: Tutoring Specifics</h3>
               <input type="text" name="subject1" placeholder="State your major subjects you handle" value={formData.subject1} onChange={handleChange} className="w-full p-2 border rounded" />            
               <input type="text" name="subject2" placeholder="State your minor subjects you handle" value={formData.subject2} onChange={handleChange} className="w-full p-2 border rounded" />
               <input type="text" name="sen_area" placeholder="State Special Education Needs you can support" value={formData.sen_area} onChange={handleChange} className="w-full p-2 border rounded" />                      
               <input type="text" name="grade_levels" placeholder="is k-5,middle school, high school, college or university" value={formData.grade_levels} onChange={handleChange} className="w-full p-2 border rounded" />           
               </div>        
               )}
              {/* STEP 5: VERIFICATION (Hidden for Learner) */}
        {step === 5 && formData.role === 'tutor' && (
                 <div className="space-y-4">
               <h3 className='font-bold border-b pb-2'>Section 5: Verification & Signature</h3>        
                <input type="text" name="crim_back_chk" placeholder="Do you consent to criminal background check if required?" value={formData.crim_back_chk} onChange={handleChange} className="w-full p-2 border rounded" />           
               <p className="text-sm italic">"I certify that my answers are true and complete"</p>
           
           <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 p-2">
             <p className="text-[10px] font-bold text-gray-500 mb-1 uppercase">Draw your signature below:</p>
             <SignatureCanvas 
               ref={sigPad} 
               penColor='black'
               canvasProps={{
                 className: 'sigCanvas w-full h-40 bg-white border rounded cursor-crosshair'
               }} 
             />
             <div className="flex justify-end mt-2">
               <button 
                 type="button" 
                 onClick={() => sigPad.current.clear()} 
                 className="text-xs font-bold text-red-500 hover:text-red-700 underline"
               >
                 Clear Signature
               </button>
             </div>
           </div>
       
           <input 
             type="text" 
             name="fullname" 
             placeholder="Type Full Name to Verify" 
             value={formData.fullname} 
             onChange={handleChange} 
             required 
             className="w-full p-2 border rounded" 
           />
         </div>
       )}
       
{/* STEP 6: FINAL REVIEW */}
        {step === 6 && (
          <div className="space-y-4 animate-in zoom-in duration-300 bg-gray-50 p-6 rounded-lg">
            <h4 className="font-bold text-gray-800">Final Review</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <ReviewItem label="Full Name" value={`${formData.first_name} ${formData.last_name}`} />
              <ReviewItem label="Email" value={formData.email} />
              {formData.role === 'tutor' && (
                <>
              <ReviewItem label="Hourly Rate" value={`${formData.currency} ${formData.hourly_rate}`} />
              <ReviewItem label="Subjects" value={formData.subject1} />
              </>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-4 italic">By clicking submit, you agree to the TRiANf {formData.role === 'tutor' ? 'Tutor' : 'Learner'} terms.</p>
          </div>
        )}

        <div className="flex justify-between items-center pt-6 border-t mt-8">
          {step > 1 && (
            <button type="button" onClick={() => setStep(step - 1)} className="px-6 py-2 text-gray-600 font-bold hover:bg-gray-100 rounded-lg transition">
              Previous
            </button>
          )}
          
          {step < 6 ? (
            <button type="button" onClick={nextStep} className="ml-auto bg-blue-600 text-white px-10 py-2 rounded-lg font-bold hover:bg-blue-700 shadow-lg transition">
              Next
            </button>
          ) : (
            <button type="submit" disabled={loading} className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 shadow-xl transition">
              {loading ? 'Processing...' : 'Complete Registration'}
            </button>
          )}
        </div>
      </form>  

      <div className="mt-8 pt-6 border-t border-gray-100 text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <button 
            type="button" // Important: keep it from triggering form submit
            onClick={() => navigate('/login')} 
            className="text-blue-600 font-semibold hover:underline"
          >
            Log in
          </button>
        </p>
      </div> 
      
      {status && (
        <div className={`mt-6 p-3 rounded-lg text-center text-sm font-bold ${status.includes('❌') ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
          {status}
        </div>
      )}
    </div>
  );
};

export default Register;